// HistoryRouter.tsx
import { useState, useLayoutEffect, useRef } from 'react';
import { Router } from 'react-router-dom';
import type { History, Action, Location } from 'history';
import useAnalytics from 'src/utils/useAnalytics';

interface HistoryRouterProps {
    basename?: string;
    children: React.ReactNode;
    history: History;
}

export default function HistoryRouter({
    basename,
    children,
    history,
}: HistoryRouterProps) {
    const [state, setState] = useState<{
        action: Action;
        location: Location;
    }>({
        action: history.action,
        location: history.location,
    });

    const prevLocationRef = useRef(history.location.pathname);

    const { logPageChange } = useAnalytics();
    // Listen for changes to the history location and update state.
    useLayoutEffect(() => {
        const unlisten = history.listen(({ location, action }) => {
            const currentPath = location.pathname;
            logPageChange(prevLocationRef.current, currentPath);
            setState({ location, action });
            prevLocationRef.current = currentPath;
        });

        return unlisten;
    }, [history, logPageChange]);

    return (
        <Router
            basename={basename}
            location={state.location}
            navigationType={state.action}
            navigator={history}
        >
            {children}
        </Router>
    );
}
